import { Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDataExport } from '~/hooks/useDataExport';

import { ExportType } from '~/data/dataExport';

import { toCamelCase } from '@vestigas/utils';
import { i18n } from '~/utils';

import {
  Button,
  DataTable,
  HeadingRow,
  Icon,
  SearchInput,
  withErrorBoundary,
} from '@vestigas/ui';

import { RowModel, USAGE_TABS } from '../constants';
import { useTabPrefetch } from '../hooks/useTabPrefetch';

import { DataCard } from './DataCard';
import { useUsageDetailsTable } from './useUsageDetailsTable';

type P = ComponentStyling;

const CardContent = withErrorBoundary(
  () => {
    const { t } = useTranslation('analytics');
    const {
      apiRef,
      columns,
      dateRange,
      detailsTable,
      getRowId,
      onChangeDetailsTab,
      onChangePage,
      onChangeSortModel,
      onSearch,
      page,
      searchInputPlaceholder,
      searchString,
      tableSortBy,
      tableSortOrder,
      usageDetails,
    } = useUsageDetailsTable();

    const exportTypeMap: Record<typeof detailsTable, ExportType> = {
      users: 'user_activity_details',
      sites: 'site_activity_details',
      costCenters: 'accounting_reference_activity_details',
    };

    const tabLabels: Record<typeof detailsTable, string> = {
      users: t(($) => $.usage.usageDetails.tabs.users),
      sites: t(($) => $.usage.usageDetails.tabs.sites),
      costCenters: t(($) => $.usage.usageDetails.tabs.costCenters),
    };

    const exportType = exportTypeMap[detailsTable];
    const tabCategory = tabLabels[detailsTable];

    const { handleExport, isLoading: isLoadingExportResult } = useDataExport();
    const { handleTabMouseDown } = useTabPrefetch({ dateRange });

    return (
      <>
        <DataCard.Header>
          <HeadingRow
            heading={t(($) => $.usage.usageDetails.heading)}
            variant="h3"
            action={
              <div className="flex flex-none gap-x-4 gap-y-1">
                <SearchInput
                  className="min-w-20 md:min-w-80 xl:min-w-96"
                  placeholder={searchInputPlaceholder}
                  debounceTime={600}
                  onChange={onSearch}
                  value={searchString}
                />
                <Button
                  className="primary-button flex-none"
                  icon={<Icon metaphor="download" />}
                  buttonStyle="primary"
                  text={t(($) => $.usage.exportXlsxButton.text)}
                  onClick={() =>
                    void handleExport({
                      name: {
                        prefix: 'Nutzung',
                        category: tabCategory,
                      },
                      exportType,
                      parameters: {
                        format: 'xlsx',
                        filters: { dateRange },
                      },
                    })
                  }
                  inProgress={isLoadingExportResult}
                />
              </div>
            }
          />
        </DataCard.Header>
        <DataCard.Body className="-mt-4 p-0">
          <Tabs
            value={detailsTable}
            onChange={(_event, newValue: (typeof USAGE_TABS)[number]) => {
              onChangeDetailsTab(newValue);
            }}
            onMouseDown={handleTabMouseDown}
            className="border-b border-gray-200"
          >
            <Tab
              id="tab--users"
              label={t(($) => $.usage.usageDetails.tabs.users)}
              value="users"
            />
            <Tab
              id="tab--sites"
              label={t(($) => $.usage.usageDetails.tabs.sites)}
              value="sites"
            />
            <Tab
              id="tab--costCenters"
              label={t(($) => $.usage.usageDetails.tabs.costCenters)}
              value="costCenters"
            />
          </Tabs>
          <DataTable<RowModel>
            apiRef={apiRef}
            columns={columns ?? []}
            getRowId={getRowId}
            paginationMeta={{ hasNextPage: usageDetails?.data?.hasNextPage }}
            loading={usageDetails?.isLoading}
            sortModel={[
              {
                field: tableSortBy ? toCamelCase(tableSortBy) : '',
                sort: tableSortOrder === 'ASC' ? 'asc' : 'desc',
              },
            ]}
            onSortModelChange={onChangeSortModel}
            paginationModel={{
              page: page,
              pageSize: 100, // this should match the "limit" of queryParam
            }}
            onPaginationModelChange={(model) => onChangePage(model.page)}
            rowCount={usageDetails?.data?.totalCount ?? 0}
            rows={usageDetails?.data?.rows ?? []}
            minHeight={40 * 11 + (52 + 40)} // 11 rows + header + footer
          />
        </DataCard.Body>
      </>
    );
  },
  i18n.t(($) => $.errorBoundary.fallbackMessage, {
    ns: 'analytics',
    section: i18n.t(($) => $.usage.usageDetails.heading, { ns: 'analytics' }),
  }),
  false,
  'row-span-3',
);

CardContent.displayName = 'UsageDetailsTableContent';

/**
 * UsageDetailsTable
 * Displays a table of usage details for the selected date range for users, sites, and cost centers.
 * It allows filtering and sorting the data and exporting it to an Excel file.
 */
export const UsageDetailsTable = withErrorBoundary(
  ({ className, style }: P) => {
    return (
      <DataCard className={className} style={style}>
        <CardContent />
      </DataCard>
    );
  },
  i18n.t(($) => $.errorBoundary.fallbackMessage, {
    ns: 'analytics',
    section: i18n.t(($) => $.usage.usageDetails.heading, { ns: 'analytics' }),
  }),
);

UsageDetailsTable.displayName = 'UsageDetailsTable';
